import { useQuery } from "@apollo/client";
import { AppliedFilters, Button, Container, Table } from "@fluidattacks/design";
import dayjs, { extend } from "dayjs";
import timezone from "dayjs/plugin/timezone";
import _ from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { FC } from "react";
import { useTranslation } from "react-i18next";

import { useEvidenceDraftsFilters } from "./filters";
import {
  GET_FINDING_EMPTY_EVIDENCE,
  GET_FINDING_EVIDENCE_DRAFTS,
} from "./queries";
import type {
  IEvidenceDrafts,
  IFindingAttr,
  IFindingEmptyEvidence,
  IFindingResponse,
  IMeFindingVariable,
} from "./types";
import { getColumns, hasEvidencesDrafts } from "./utils";

import { GET_USER_ORGANIZATIONS_GROUPS } from "../queries";
import type { IGroups, IOrganizationGroups } from "@types";
import { getOrganizationGroups } from "features/vulnerabilities/utils";
import { useAudit } from "hooks/use-audit";
import { useTable } from "hooks/use-table";
import {
  formatEvidenceImages,
  formatEvidenceList,
} from "pages/finding/evidence/utils";
import { Logger } from "utils/logger";

const EvidenceDrafts: FC = (): JSX.Element => {
  extend(timezone);
  const { t } = useTranslation();
  const { addAuditEvent } = useAudit();
  const [search, setSearch] = useState("");
  const [filteredData, setFilteredData] = useState<IFindingAttr[]>([]);

  const { data, loading, fetchMore, refetch } = useQuery<
    IEvidenceDrafts,
    IMeFindingVariable
  >(GET_FINDING_EVIDENCE_DRAFTS, {
    fetchPolicy: "cache-first",
    onCompleted: (): void => {
      addAuditEvent("ToDo.EvidenceDrafts", "unknown");
    },
    onError: ({ graphQLErrors }): void => {
      graphQLErrors.forEach((error): void => {
        Logger.error(
          "An error occurred loading finding evidence drafts",
          error,
        );
      });
    },
    pollInterval: 60000,
    variables: { first: 100 },
  });

  const {
    data: dataEmpty,
    fetchMore: fetchMoreEmpty,
    refetch: refetchEmpty,
  } = useQuery<IFindingEmptyEvidence, IMeFindingVariable>(
    GET_FINDING_EMPTY_EVIDENCE,
    {
      fetchPolicy: "cache-first",
      onError: ({ graphQLErrors }): void => {
        graphQLErrors.forEach((error): void => {
          Logger.error(
            "An error occurred loading finding empty evidence",
            error,
          );
        });
      },
      pollInterval: 60000,
      variables: { first: 100 },
    },
  );

  const { data: userData } = useQuery(GET_USER_ORGANIZATIONS_GROUPS, {
    fetchPolicy: "cache-first",
    onError: ({ graphQLErrors }): void => {
      graphQLErrors.forEach((error): void => {
        Logger.warning("An error occurred fetching user groups", error);
      });
    },
  });

  const refreshDrafts = useCallback((): void => {
    refetch().catch((): void => {
      Logger.error("An error occurred in refetch");
    });
    refetchEmpty().catch((): void => {
      Logger.error("An error occurred loading finding empty evidence");
    });
  }, [refetch, refetchEmpty]);

  const formatEdges = useCallback(
    (edge: { node: IFindingResponse }): IFindingAttr => {
      const evidenceImages = formatEvidenceImages(edge.node.evidence);
      const evidenceList = formatEvidenceList(evidenceImages, false, true);
      const organizationGroup = getOrganizationGroups(
        (userData?.me.organizations ?? []).map(
          (org): IOrganizationGroups => ({
            __typename: "Organization",
            groups: org.groups.map(
              (group): IGroups => ({
                __typename: "Group",
                name: group.name,
                permissions: group.permissions,
                serviceAttributes: group.serviceAttributes,
              }),
            ),
            name: org.name,
          }),
        ),
        edge.node.groupName,
      );

      return {
        ...edge.node,
        hasApprovedEvidence: !_.isEmpty(evidenceList),
        hasEvidenceDraft: hasEvidencesDrafts(evidenceImages),
        hasLocationApproved:
          edge.node.vulnerabilitiesSummary.closed +
            edge.node.vulnerabilitiesSummary.open >
          0,
        hasLocationDrafts:
          edge.node.rejectedVulnerabilities +
            edge.node.submittedVulnerabilities >
          0,
        organizationName:
          organizationGroup === undefined ? null : organizationGroup.name,
      };
    },
    [userData],
  );
  const findingEvidenceDraftsPageInfo =
    data === undefined ? undefined : data.me.findingEvidenceDrafts.pageInfo;
  const findingEmptyEvidencePageInfo =
    dataEmpty === undefined
      ? undefined
      : dataEmpty.me.findingEmptyEvidence.pageInfo;

  useEffect((): void => {
    if (!_.isUndefined(findingEvidenceDraftsPageInfo)) {
      if (findingEvidenceDraftsPageInfo.hasNextPage) {
        void fetchMore({
          variables: {
            after: findingEvidenceDraftsPageInfo.endCursor,
            first: 1200,
          },
        });
      }
    }
  }, [findingEvidenceDraftsPageInfo, fetchMore]);

  useEffect((): void => {
    if (!_.isUndefined(findingEmptyEvidencePageInfo)) {
      if (findingEmptyEvidencePageInfo.hasNextPage) {
        void fetchMoreEmpty({
          variables: {
            after: findingEmptyEvidencePageInfo.endCursor,
            first: 1200,
          },
        });
      }
    }
  }, [findingEmptyEvidencePageInfo, fetchMoreEmpty]);

  const { edges } =
    data === undefined ? { edges: [] } : data.me.findingEvidenceDrafts;
  const { edges: edgesEmpty } =
    dataEmpty === undefined ? { edges: [] } : dataEmpty.me.findingEmptyEvidence;

  const formattedData = useMemo(
    (): IFindingAttr[] =>
      [...edges.map(formatEdges), ...edgesEmpty.map(formatEdges)].filter(
        ({ groupName, title }): boolean =>
          groupName.toLowerCase().includes(search.toLowerCase()) ||
          (title?.toLowerCase().includes(search.toLowerCase()) ?? false),
      ),
    [edges, edgesEmpty, formatEdges, search],
  );

  const { Filters, options, removeFilter } = useEvidenceDraftsFilters(
    formattedData,
    setFilteredData,
  );
  const { table } = useTable({
    columns: getColumns(),
    data: filteredData,
    id: "evidence-drafts-table",
    options: {
      actionsOptions: { onSearch: setSearch, search },
      filtersMode: "client-side",
      loading,
      paginationMode: "client-side",
    },
    total: filteredData.length,
  });

  return (
    <Table table={table}>
      <Table.Options>
        <Table.Controls>
          <Filters />
          <Container gap={0.5} id={"actions-container"}>
            <Table.Export
              csvConfig={{
                columns: [
                  "groupName",
                  "title",
                  "id",
                  "hasEvidenceDraft",
                  "hasLocationDrafts",
                  "hasApprovedEvidence",
                  "hasLocationApproved",
                ],
                export: true,
                headers: {
                  groupName: "Group name",
                  hasApprovedEvidence: t(
                    "todoList.tabs.evidenceDrafts.table.headers.hasApprovedEvidence",
                  ),
                  hasEvidenceDraft: t(
                    "todoList.tabs.evidenceDrafts.table.headers.hasEvidenceDraft",
                  ),
                  hasLocationApproved: t(
                    "todoList.tabs.evidenceDrafts.table.headers.hasLocationApproved",
                  ),
                  hasLocationDrafts: t(
                    "todoList.tabs.evidenceDrafts.table.headers.hasLocationDrafts",
                  ),
                  id: "Id",
                  title: "Weakness",
                },
                name: `evidence-drafts-${dayjs().tz(dayjs.tz.guess()).format("YYYY-MM-DD")}`,
              }}
              data={filteredData}
            >
              <Button
                icon={"file-export"}
                id={"CSVExportBtn"}
                tooltip={t("buttons.exportTip", { format: "CSV" })}
                variant={"ghost"}
              >
                {t("group.findings.exportCsv.text")}
              </Button>
            </Table.Export>
            <Button icon={"refresh"} onClick={refreshDrafts} variant={"ghost"}>
              {t("todoList.assignedVulnerabilities.refresh")}
            </Button>
          </Container>
        </Table.Controls>
      </Table.Options>
      <Table.Caption>
        <AppliedFilters onClose={removeFilter} options={options} />
      </Table.Caption>
      <Table.Content />
      <Table.Footer />
    </Table>
  );
};

export { EvidenceDrafts };
